import { t } from 'i18next';
import { Box, Typography, Tab, Button } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import { IColumnDefinition } from '@common/types';

import { usePatients } from './hooks';
import { CarePlanDownloadModal, PatientsTable } from './components';
import { CreatePatient } from './CreatePatient';
import { RecentPatientTable } from '@views/pages/Patients/components/RecentPatientTable';
import { formatCount } from '@util/number';

const columns: IColumnDefinition[] = [
  {
    id: 'id',
    label: 'ID',
    sortable: false,
  },
  {
    id: 'name',
    label: 'Name',
    sortable: true,
    width: 120,
  },
  {
    id: 'birthdate',
    label: 'Date of birth',
    sortable: true,
    width: 130,
  },
  {
    id: 'minutes_this_month',
    label: 'CM Minutes',
    sortable: true,
    align: 'center',
  },
  {
    id: 'mbi',
    label: 'Primary insurance',
    sortable: false,
  },
  {
    id: 'account_status',
    label: 'Account status',
    sortable: false,
    width: 120,
    align: 'center',
  },
  {
    id: 'guide_status',
    label: 'GUIDE status',
    sortable: false,
  },
  {
    id: 'programs',
    label: 'Programs',
    sortable: false,
  },
  {
    id: 'open_task_count',
    label: 'Open tasks',
    sortable: true,
  },
  {
    id: 'provider_name',
    label: 'PCP',
    sortable: true,
    width: 120,
  },
  {
    id: 'practice_name',
    label: 'Practice',
    sortable: true,
  },
  {
    id: 'care_navigator_name',
    label: 'Care navigator',
    sortable: true,
    width: 150,
  },
  {
    id: 'building_name',
    label: 'Location',
    sortable: true,
    width: 150,
  },
];

export const Patients = () => {
  const {
    allPatientCount,
    myPatientCount,
    tab,
    handleChangeTab,
    handleOpenDownloadModal,
    allowDownloadOwnPatientsCarePlans,
    allowDownloadTeamPatientsCarePlans,
    handleOpenCarePlanDownloadModal,
    handleCloseCarePlaneDownloadModal,
    isDownloadCarePlanModalOpen,
  } = usePatients();

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        bgcolor: theme => theme.palette.grey[100],
      }}
    >
      <Box
        sx={{
          paddingTop: '1rem',
          gap: '1rem',
          display: 'flex',
          flexDirection: 'column',
          pb: '1rem',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='h4'>Patients</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              sx={{ height: 42 }}
              variant='outlined'
              onClick={handleOpenCarePlanDownloadModal}
            >
              {t('patients.carePlanDownloadModal.button')}
            </Button>
            <CreatePatient />
          </Box>
        </Box>
      </Box>
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleChangeTab}>
            <Tab label={t('patients.recent')} value='recent' />
            <Tab
              label={t('patients.allPatients', {
                number: formatCount(allPatientCount),
              })}
              value='all'
            />
            <Tab
              label={t('patients.myPatients', {
                number: formatCount(myPatientCount),
              })}
              value='mine'
            />
          </TabList>
        </Box>
        <TabPanel value='recent'>
          <RecentPatientTable columns={columns} />
        </TabPanel>
        <TabPanel value='all'>
          <PatientsTable
            variant='allPatients'
            downloadAction={handleOpenDownloadModal}
          />
        </TabPanel>
        <TabPanel value='mine'>
          <PatientsTable variant='myPatients' />
        </TabPanel>
      </TabContext>
      <CarePlanDownloadModal
        isOpen={isDownloadCarePlanModalOpen}
        onClose={handleCloseCarePlaneDownloadModal}
        ownPatients={allowDownloadOwnPatientsCarePlans}
        teamPatients={allowDownloadTeamPatientsCarePlans}
      />
    </Box>
  );
};
